import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import { Col, List, Row } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { throttle } from 'lodash-es';
import { useLocation } from 'react-router';
import { TrackerCustomEvent, useTracker } from 'common-package';
import { generateDateRange } from '@/utils';
import { Calendar, Header, HeaderMemberInfo, ScrollView } from '@/components';
import { DAYJS_FORMAT, MassageType2Text } from '@/constant';
import { MemberDrawer } from '@/pages/user/member-detail/member-drawer';
import { useNav } from '@/hooks';
import service from '@/service';
import { ScrollViewRefAttrs } from '@/components/scroll-view';
import { CalendarProps } from '@/components/calendar';
import ServiceRecordDrawer, { ServiceRecordDrawerRefAttrs } from '../ServiceRecordDrawer';
import EmptyBox from '../EmptyBox';
import MemberOrderInfoItem from './MemberOrderInfoItem';
import { getConsumptionOption, getConsumptionTimesOption } from './echartsOption';
import cx from './index.module.scss';

// 默认排序规则
export const MASSAGE_TYPE_ORDER = ['Moxibustion', 'HotStone', 'RadioFrequency', 'Shockwave', 'Massage'];

// 寻找用户中消费比例最高的一个属性名,如果最高值有多于一个 则返回true
const maxValueObject = (data: GetMemberConsumptionInfoResponse[]) => {
  if (data?.length > 0) {
    const maxVal = Math.max(...data?.map(item => item.totalOrders));
    const isMaxValueEqual = data?.filter(item => item.totalOrders === maxVal).length > 1;
    const MassageTypeMax = data?.find(item => item.totalOrders === maxVal)?.itemType;
    const result = isMaxValueEqual ? true : MassageTypeMax;
    return result;
  } else {
    return false;
  }
};

interface Props {}

const INIT_LOAD_SIZE = 9;

const MemberRecordDetail: React.FC<Props> = props => {
  const { state } = useLocation();

  const nav = useNav();
  const { sendEvent } = useTracker();
  const chartRef = useRef(null);
  const chartTimesRef = useRef(null);
  const scrollViewRef = useRef<ScrollViewRefAttrs>(null);
  const serviceRecordDrawerRef = useRef<ServiceRecordDrawerRefAttrs>(null);
  const [isLoading, setLoading] = useState<boolean>(true);
  const [scrollHeight, setScrollHeight] = useState<number>(0);
  const [disableDate, setDisableDate] = useState<GetCustomerTimeRange>(null);
  const [isDrawerShow, setIsDrawerShow] = useState(false);
  const [timeRange, setTimeRange] = useState<CalendarProps['value']>(null);
  const [consumptionInfo, setConsumptionInfo] = useState<GetMemberConsumptionInfoResponse[]>([]);
  const [memberOrderList, setMemberOrderList] = useState<MemberOrderItem[]>([]);
  const [hasMoreData, setHasMoreData] = useState<boolean>(true);
  const [dateChartData, setDateChartData] = useState<string[]>([]);
  const [currentCustomerInfo, setCurrentCustomerInfo] = useState<GetCustomerProfileResponse>(state?.customerInfo);

  const option = useMemo(() => {
    return getConsumptionTimesOption(consumptionInfo, dateChartData);
  }, [consumptionInfo, dateChartData]);

  const selectDate = useMemo(
    () => (timeRange as [Dayjs, Dayjs])?.map(item => item?.format(DAYJS_FORMAT.DATE) ?? null) || [],
    [timeRange]
  );

  const getSelectedTime = (selectDate: string[]) => {
    let startDate = dayjs(selectDate?.[0]);
    let endDate = dayjs(selectDate?.[1]);

    if (startDate.isAfter(endDate)) {
      startDate = dayjs(selectDate?.[1]);
      endDate = dayjs(selectDate?.[0]);
    }

    return { startDate, endDate };
  };

  const getCustomTimeRange = async customerId => {
    try {
      const { data } = await service.serviceRecordApi.getMemberTimeRange(customerId);
      setDisableDate(data);
      // 兼容特殊情况对于没有下单次数的会员 对比一周前的开始时间是否在返回的数据内
      if (
        dayjs(data?.availableEndTime).add(-6, 'd').isBefore(dayjs(data?.availableEndTime)) &&
        dayjs(data?.availableEndTime).add(-6, 'd').isAfter(dayjs(data?.availableStartTime))
      ) {
        setTimeRange([dayjs(data?.availableEndTime).add(-6, 'd'), dayjs(data?.availableEndTime)]);
      } else {
        setTimeRange([dayjs(data?.availableStartTime), dayjs(data?.availableEndTime)]);
      }
    } catch (e) {}
  };

  const getChartData = async (customerId: number, startDate: string, endDate: string) => {
    try {
      const { data } = await service.serviceRecordApi.getMemberConsumptionInfo({
        customerId,
        startDate,
        endDate,
      });
      data.sort((a, b) => MASSAGE_TYPE_ORDER.indexOf(a.itemType) - MASSAGE_TYPE_ORDER.indexOf(b.itemType));
      setConsumptionInfo(data);
    } catch (e) {}
  };

  const getMemberOrderListByDate = useCallback(
    async (params: GetMemberOrderInfoParams, reset: boolean = false) => {
      try {
        reset && setLoading(true);
        const { data } = await service.serviceRecordApi.getMemberOrderByDate(params);
        reset ? setMemberOrderList(data) : setMemberOrderList([...memberOrderList, ...data]);
        const _hasMoreData = data?.length >= params?.pageSize;
        setHasMoreData(_hasMoreData);
        if (data?.length > 0) {
          scrollViewRef.current?.handleRefresh();
        }

        if (_hasMoreData) {
          scrollViewRef.current?.bsInstance?.finishPullUp();
        }
      } finally {
        reset && setLoading(false);
      }
    },
    [memberOrderList]
  );

  const loadMore = useMemo(
    () =>
      throttle(async () => {
        const lastCursor = memberOrderList[memberOrderList?.length - 1]?.serviceOrderId;
        if (selectDate?.filter(Boolean)?.length === 2) {
          let startDate = getSelectedTime(selectDate).startDate;
          let endDate = getSelectedTime(selectDate).endDate;
          getMemberOrderListByDate({
            pageSize: INIT_LOAD_SIZE,
            customerId: state?.customerInfo?.customerId,
            startDate: startDate.format('YYYY-MM-DD'),
            endDate: endDate.format('YYYY-MM-DD'),
            cursor: lastCursor,
          });
        }
      }, 600),
    [memberOrderList, selectDate, getMemberOrderListByDate, state?.customerInfo?.customerId]
  );

  const getDisableCalendarDate = useCallback(
    (current: dayjs.Dayjs) => {
      if (selectDate?.filter(Boolean)?.length === 1) {
        // 选择的区域最大跨度为半年
        const availableHalfYearStart = dayjs(selectDate?.[0]).add(-180, 'd');
        const availableHalfYearEnd = dayjs(selectDate?.[0]).add(+180, 'd');
        const latestStart = availableHalfYearStart.isAfter(dayjs(disableDate?.availableStartTime))
          ? availableHalfYearStart
          : dayjs(disableDate?.availableStartTime);
        const earliestEnd = availableHalfYearEnd.isBefore(dayjs(disableDate?.availableEndTime))
          ? availableHalfYearEnd
          : dayjs(disableDate?.availableEndTime);

        return current && (current > dayjs(earliestEnd) || current < dayjs(latestStart));
      } else {
        return (
          current &&
          (current < dayjs(disableDate?.availableStartTime) || current > dayjs(disableDate?.availableEndTime))
        );
      }
    },
    [disableDate?.availableEndTime, disableDate?.availableStartTime, selectDate]
  );

  const handleViewMemberServiceInfo = useCallback(
    (serviceOrderId: string) => () => {
      serviceRecordDrawerRef.current?.show(serviceOrderId);
      sendEvent(TrackerCustomEvent.MemberConsumptionDetailClk, {
        et: 'CLK',
      });
    },
    [sendEvent]
  );

  const getMemberOrderList = useCallback(() => {
    // 会员记录信息关闭时候重新获取数据
    let startDate = getSelectedTime(selectDate)?.startDate;
    let endDate = getSelectedTime(selectDate)?.endDate;

    getMemberOrderListByDate(
      {
        pageSize: INIT_LOAD_SIZE,
        customerId: state?.customerInfo?.customerId,
        startDate: startDate.format('YYYY-MM-DD'),
        endDate: endDate.format('YYYY-MM-DD'),
        cursor: '',
      },
      true
    );
  }, [getMemberOrderListByDate, selectDate, state?.customerInfo?.customerId]);

  const getServiceCustomerInfo = async customerId => {
    const { data } = await service.customer.getCustomerProfile(customerId);
    setCurrentCustomerInfo(data);
    setIsDrawerShow(true);
  };

  useEffect(() => {
    getCustomTimeRange(state?.customerInfo?.customerId);
  }, [state?.customerInfo?.customerId]);

  useEffect(() => {
    if (selectDate?.filter(Boolean)?.length === 2) {
      let startDate = getSelectedTime(selectDate).startDate;
      let endDate = getSelectedTime(selectDate).endDate;

      setDateChartData(generateDateRange(startDate.valueOf(), endDate.valueOf())?.reverse());

      getChartData(state?.customerInfo?.customerId, startDate.format('YYYY-MM-DD'), endDate.format('YYYY-MM-DD'));
      getMemberOrderListByDate(
        {
          pageSize: INIT_LOAD_SIZE,
          customerId: state?.customerInfo?.customerId,
          startDate: startDate.format('YYYY-MM-DD'),
          endDate: endDate.format('YYYY-MM-DD'),
          cursor: '',
        },
        true
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.customerInfo?.customerId, selectDate]);

  useEffect(() => {
    const scrollHeight = document.documentElement.scrollHeight;
    setScrollHeight(scrollHeight);
  }, []);

  return (
    <div className={cx('member-record-detail')} style={{ height: scrollHeight + 'px' }}>
      <Header
        title={
          <HeaderMemberInfo
            name={state?.customerInfo?.name}
            avatarUrl={state?.customerInfo?.avatarUrl ?? ''}
            gender={state?.customerInfo?.gender}
            textContent="的记录详情"
            handleClick={() => getServiceCustomerInfo(state?.customerInfo?.id)}
          />
        }
        backConfig={{
          text: '服务详情',
          //返回会员记录页面
          action: () => nav('/service-record#member-record', { state: state }),
        }}
      />
      <div className={cx('calendar-wrapper')}>
        <Calendar
          className={cx('calendar')}
          mode="range"
          value={timeRange}
          onChange={setTimeRange}
          disabledDate={current => getDisableCalendarDate(current)}
          allowClear={false}
          cancelSelected
        />
      </div>

      <ScrollView className={cx('list-wrapper')} ref={scrollViewRef} onPullingUp={loadMore}>
        {memberOrderList?.length ? (
          <div className={cx('content')}>
            <div className={cx('consumption-wrapper')}>
              <Row>
                <Col span={7}>
                  <div className={cx('echarts-title')}>消费偏好</div>
                  <div className={cx('echarts-wrapper')}>
                    <div
                      className={cx('order-top-one', {
                        hide: (maxValueObject(consumptionInfo) as boolean) === true,
                      })}
                    >
                      <div>下单意愿Top1</div>
                      <div>{MassageType2Text[maxValueObject(consumptionInfo) as string]}</div>
                    </div>
                    <ReactECharts
                      ref={chartRef}
                      option={getConsumptionOption(consumptionInfo)}
                      style={{ height: '100%' }}
                      opts={{ renderer: 'svg' }}
                      lazyUpdate={true}
                      notMerge={false}
                    />
                  </div>
                </Col>
                <Col span={17}>
                  <div className={cx('echarts-times-wrapper')}>
                    <div className={cx('echarts-title')}>
                      <span>消费频次监控</span>
                      <span>（单位：单）</span>
                    </div>
                    <ReactECharts
                      ref={chartTimesRef}
                      option={option}
                      style={{ height: '100%' }}
                      opts={{ renderer: 'svg' }}
                      lazyUpdate={true}
                      notMerge={false}
                    />
                  </div>
                </Col>
              </Row>
            </div>
            <List>
              <div className={cx('member-order-list')}>
                {memberOrderList?.map((item, index) => (
                  <MemberOrderInfoItem
                    key={index}
                    {...item}
                    handleViewInfo={handleViewMemberServiceInfo(item?.serviceOrderId)}
                  />
                ))}
              </div>
            </List>

            <div className={cx('load-data-info')}>{hasMoreData ? '正在加载更多...' : ''}</div>
          </div>
        ) : isLoading ? (
          <div className={cx('skeleton-wrapper')}>
            <div className={cx('eChart-wrapper')} />
            <div className={cx('record-wrapper')}>
              {[...new Array(6)].map((_, key) => (
                <div key={key} />
              ))}
            </div>
          </div>
        ) : (
          <EmptyBox className={cx('empty-wrapper')} />
        )}
      </ScrollView>

      <ServiceRecordDrawer
        ref={serviceRecordDrawerRef}
        refreshList={() => {
          getMemberOrderList();
          scrollViewRef.current?.bsInstance?.scrollTo(0, 0);
        }}
      />

      <MemberDrawer
        open={isDrawerShow}
        memberInfo={currentCustomerInfo}
        handleClose={() => setIsDrawerShow(false)}
        isServicing={true}
      />
    </div>
  );
};

export default MemberRecordDetail;
